<template>
    <div class="product-v3-container">
        <!-- Stats Header Cards -->
        <ExperienceStatsNavigation
            :stats="stats"
            :activeTab="activeTab"
            @click="setActiveTab"
            :can-consult-experience="canConsultExperience"
            :can-consult-forfait="canConsultForfait"
            :can-consult-formule="canConsultFormule"
            :can-consult-abonnement="canConsultAbonnement"
            :can-consult-categories="canConsultCategories"
        />

        <!-- Main Content Card -->
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <EnhancementBanner :isVisible="isEnhance" />

            <!-- Tab Title (hidden when viewing subcategories or breadcrumbs) -->
            <div v-if="!showSubcategories && !hasBreadcrumb" class="ppv2-flex ppv2-items-center ppv2-justify-between">
                <Header :title="tabTitle" />
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent"
                ></div>
            </div>

            <!-- Tab Content -->
            <component
                v-else-if="!showSubcategories"
                :is="currentTabComponent"
                :key="activeTab"
                :user-id="userId"
                :initial-type="currentTabInitialType"
                source="experience"
                @refresh-stats="fetchStats"
                @view-subcategories="handleViewSubcategories"
                @breadcrumb-change="handleBreadcrumbChange"
                :url-front-reservation="urlFrontReservation"
                :token="token"
                :can-consult-forfait="canConsultForfait"
                :can-consult-formule="canConsultFormule"
                :can-consult-abonnement="canConsultAbonnement"
                :can-consult-experience="canConsultExperience"
            />

            <!-- Subcategories View -->
            <PrestationsSubcategoriesTable
                v-else
                :category-id="selectedCategoryId"
                @back="showSubcategories = false"
                @refresh-stats="fetchStats"
            />
        </div>
    </div>
</template>

<script>
import ExperienceTable from './ExperiencesTable.vue'
import EquipementsTable from '../Equipements/index.vue'
import PrestationCategoriesTable from './PrestationCategoriesTable.vue'
import PrestationsSubcategoriesTable from './PrestationsSubcategoriesTable.vue'
import ExperienceStatsNavigation from './ExperienceStatsNavigation.vue'
import EnhancementBanner from '../../Shared/EnhancementBanner.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import Header from '../../Shared/Header.vue'
export default {
    name: 'ExperienceV3Index',
    components: {
        ExperienceTable,
        ExperienceStatsNavigation,
        EquipementsTable,
        PrestationCategoriesTable,
        PrestationsSubcategoriesTable,
        EnhancementBanner,
        Header,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        initialTab: {
            type: String,
            default: 'experiences',
        },
        urlFrontReservation: {
            type: String,
            default: '',
        },
        token: {
            type: String,
            default: '',
        },
        canConsultExperience: {
            type: [Boolean, String],
            default: true,
        },
        canConsultCategories: {
            type: [Boolean, String],
            default: true,
        },
        canConsultForfait: {
            type: [Boolean, String],
            default: true,
        },
        canConsultFormule: {
            type: [Boolean, String],
            default: true,
        },
        canConsultAbonnement: {
            type: [Boolean, String],
            default: true,
        },
    },
    data() {
        const tabMapping = {
            experience: 'experiences',
            tags: 'categories',
        }
        return {
            activeTab: tabMapping[this.initialTab] || this.initialTab,
            isLoading: true,
            isEnhance: false,
            showSubcategories: false,
            selectedCategoryId: null,
            stats: {
                experiences: 0,
                installations: 0,
                equipements: 0,
                categories: 0,
            },
            hasBreadcrumb: false,
        }
    },
    computed: {
        currentTabComponent() {
            const components = {
                experiences: 'ExperienceTable',
                installations: 'EquipementsTable',
                equipements: 'EquipementsTable',
                categories: 'PrestationCategoriesTable',
            }
            return components[this.activeTab] || 'ExperienceTable'
        },
        pageTitle() {
            const titles = {
                experiences: 'experiences',
            }
            return titles[this.activeTab] || 'Experiences'
        },
        tabTitle() {
            const titles = {
                experiences: this.$t('experiences.navigation.experiences'),
                installations: this.$t('experiences.navigation.installations'),
                equipements: this.$t('experiences.navigation.equipements'),
                categories: this.$t('experiences.navigation.categories'),
            }
            return titles[this.activeTab] || this.$t('experiences.navigation.experiences')
        },
        currentTabInitialType() {
            const types = {
                experiences: 'tout',
                installations: 'salle',
                equipements: 'materiel',
            }
            return types[this.activeTab] || 'tout'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        setActiveTab(tab) {
            this.activeTab = tab
            this.showSubcategories = false
            this.hasBreadcrumb = false

            // Update URL query parameter without reloading
            const url = new URL(window.location)
            const tabMapping = {
                experiences: 'experience',
                categories: 'tags',
            }
            url.searchParams.set('tab', tabMapping[tab] || tab)
            window.history.pushState({}, '', url)
        },
        handleBreadcrumbChange(hasBreadcrumb) {
            this.hasBreadcrumb = hasBreadcrumb
        },
        handleViewSubcategories(categoryId) {
            this.selectedCategoryId = categoryId
            this.showSubcategories = true
        },
        async fetchStats() {
            try {
                const response = await axios.get('/api/v3/experience/stats', {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                this.stats = response.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
    },
    mounted() {
        this.fetchStats()
        // Remove white background from parent container for V3 layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('product-v3-no-bg')
        }
    },
    beforeDestroy() {
        // Restore background when leaving the page
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('product-v3-no-bg')
        }
    },
}
</script>

<style scoped lang="scss">
.product-v3-container {
    padding: 0;
}
</style>

<style lang="scss">
#main-container.product-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

#main-container:has(.product-v3-container) {
    background: transparent !important;
}

.dark #main-container.product-v3-no-bg,
.dark #main-container:has(.product-v3-container) {
    background: transparent !important;
}

#page-header {
    display: none;
}
</style>
